import { post } from 'app-core/data';
import { eventsBus } from 'app-core/config';

let voices = ['alloy', 'ash', 'ballad', 'coral', 'echo', 'sage', 'shimmer', 'verse'];

let lastSession = null;
async function getSession(info) {
	if (lastSession) return lastSession;
	const payload = {
		model: 'gpt-realtime-mini-2025-12-15',
		voice: 'alloy',
		...(info || {})
	};
	const response = await post('OpenAiRealtime/CreateSessions', payload);
	lastSession = response;
	return lastSession;
}

let ephemeralKey = null;
async function authKey(opts) {
	const session = await getSession(opts?.sessionSettings);
	ephemeralKey = session?.client_secret?.value;
	return ephemeralKey;
}

const OpenAIWebRTC = (() => {
	let pc = null;
	let dc = null;
	let initPromise = null;
	const pendingCallbacks = [];

	let tools = {};

	let lastInitOpts = null;
	const mediaResources = {
		streams: [],       // MediaStreams
		tracks: [],        // MediaStreamTracks
		audioContexts: [], // AudioContext instances
		analyserNodes: [], // AnalyserNodes if any
		gainNodes: [],     // GainNodes if any
		connections: []    // Any other audio nodes/connections
	};
	async function init(opts) {
		if (pc && dc && dc.readyState === "open") return;
		if (initPromise) return initPromise;

		lastInitOpts = opts;

		if (opts?.tools) {
			tools = Object.keys(opts.tools).reduce((res, v) => {
				if (opts.tools[v].handler) {
					res[v] = opts.tools[v].handler;
				}
				return res;
			}, {});

			if (__DEBUG__)
				console.debug('AIRealtime tools', { tools, opts });
		}

		initPromise = (async () => {
			await authKey(opts);
			pc = new RTCPeerConnection();
			const audioEl = document.createElement("audio");
			audioEl.autoplay = true;
			pc.ontrack = (e) => {
				mediaResources.streams.push(e.streams[0]);
				e.streams[0].getTracks().forEach(track => {
					mediaResources.tracks.push(track);
				});
				audioEl.srcObject = e.streams[0];
				if (opts.customPlay) {
					opts.customPlay(e.streams[0]);
				}

				if (__DEBUG__)
					console.debug('speakMediaStream ontrack', e);
			};
			const ms = await navigator.mediaDevices.getUserMedia({ audio: {
				audio: {
					echoCancellation: true,
					noiseSuppression: true,
					autoGainControl: true,
					googEchoCancellation: true,
					googNoiseSuppression: true,
					googAutoGainControl: true,
					googHighpassFilter: true
				}
			} });

			mediaResources.streams.push(ms);
			ms.getTracks().forEach(track => {
				mediaResources.tracks.push(track);
			});

			pc.addTrack(ms.getTracks()[0]);
			dc = pc.createDataChannel("oai-events");
			const dataChannelOpen = new Promise((resolve) => {
				dc.onopen = () => {
					eventsBus.emit('open-ai-realtime--events', `info`, 'WebRTC connection opened');
					resolve();
				};
			});
			dc.onclose = () => {
				let unexpectedClose = !!initPromise;

				eventsBus.emit('open-ai-realtime--events', `info`, 'WebRTC connection closed');
				pc = null;
				dc = null;
				initPromise = null;

				if (unexpectedClose) {
					if (__DEBUG__)
						console.debug('Unexpected close');

					setTimeout(() => {
						initSpeaker(lastInitOpts);

						eventsBus.emit('open-ai-realtime--events', `info`, 'Try to reopen');
					}, 500);
				}
			};
			dc.onmessage = async (e) => {
				try {
					const data = JSON.parse(e.data);
					if (pendingCallbacks.length > 0) {
						const cb = pendingCallbacks.shift();
						cb(data);
					}

					if (__DEBUG__)
						console.log("Received message:", data);

					if (opts?.processData) {
						opts?.processData(data);
					}

					if (data?.type === 'response.function_call_arguments.done') {
						let functionName = data.name;
						let res = null;

						if (tools[functionName]) {
							res = await tools[functionName](JSON.parse(data.arguments), opts, (opts.tools || {})[functionName], dc);
							const evt = {
								type: 'conversation.item.create',
								item: {
									type: 'function_call_output',
									call_id: data.call_id,
									output: typeof(res) === 'string' ? res : JSON.stringify(res)
								}
							}

							eventsBus.emit('open-ai-realtime--events', `tool`, {
								tool: functionName,
								arguments: data.arguments,
								output: evt.item.output
							});

							dc.send(JSON.stringify(evt));
							makeResponse();
						} else {
							eventsBus.emit('open-ai-realtime--events', `tool`, `${functionName} tool not implemented`);
						}

						if (__DEBUG__)
							console.debug(`AIRealtimeInfo ${functionName}${!tools[functionName] ? ' NOT_IMPLEMENTED' : ''}`, { input: JSON.parse(data.arguments), output: res });
					}

					if (data?.type === 'response.done') {
						let resp = data?.response;
						if (resp?.modalities?.includes('text')) {
							let transcript = ((resp?.output || [])[0]?.content || [])[0]?.transcript;
							if (transcript) {
								if (__DEBUG__)
									console.debug(`AIRealtimeInfo transcript`, transcript);
								if (opts?.processTranscript) {
									opts?.processTranscript(transcript, 'response');
								}

								eventsBus.emit('open-ai-realtime--events', 'response', transcript);
							}
						}
					}

					if (data?.type === 'conversation.item.input_audio_transcription.completed') {
						let transcript = data?.transcript;

						if (opts?.processTranscript) {
							opts?.processTranscript(transcript, 'user');
						}

						eventsBus.emit('open-ai-realtime--events', 'user', transcript);
					}
				} catch (error) {
					console.error("Error parsing message:", error);
				}
			};
			const offer = await pc.createOffer();
			await pc.setLocalDescription(offer);
			const baseUrl = "https://api.openai.com/v1/realtime";
			const model = "gpt-realtime-mini-2025-12-15";
			const sdpResponse = await fetch(`${baseUrl}?model=${model}`, {
				method: "POST",
				body: offer.sdp,
				headers: {
					Authorization: `Bearer ${ephemeralKey}`,
					"Content-Type": "application/sdp"
				}
			});
			const answer = {
				type: "answer",
				sdp: await sdpResponse.text()
			};
			await pc.setRemoteDescription(answer);
			await dataChannelOpen;
		})();
		return initPromise;
	}

	async function sendMessage(message) {
		if (!pc || !dc || dc.readyState !== "open") {
			await init();
		}
		if (dc.readyState !== "open") {
			throw new Error("Data channel not open");
		}
		return new Promise((resolve, reject) => {
			pendingCallbacks.push(resolve);
			try {
				dc.send(JSON.stringify(message));
			} catch (error) {
				const index = pendingCallbacks.indexOf(resolve);
				if (index > -1) {
					pendingCallbacks.splice(index, 1);
				}
				reject(error);
			}
		});
	}

	function makeResponse() {
		dc.send(JSON.stringify({
			type: 'response.create'
		}));
	}

	async function sendText(text) {
		if (__DEBUG__)
			console.debug(`AIRealtimeInfo sendText`, text);
		await sendMessage({
			type: "response.create",
			response: {
				modalities: ["text", "audio"],
				instructions: text,
			},
		});
	}

	async function closeSession() {
		// Store the current state of connections to check if they're actually being closed
		const hadDC = !!dc;
		const hadPC = !!pc;
		
		initPromise = null;
		lastSession = null;
		
		// Handle data channel closure
		if (dc) {
			try {
				// Create a promise that resolves when data channel closes or after timeout
				const dataChannelClosePromise = new Promise((resolve) => {
					// If already closed, resolve immediately
					if (dc.readyState === 'closed') {
						resolve();
						return;
					}
					
					// Set up event listener for proper closure
					const onClose = () => {
						if (dc) {
							dc.removeEventListener('close', onClose);
						}
						resolve();
					};
					dc.addEventListener('close', onClose);
					
					// Call close (synchronous method)
					dc.close();
					
					// Add a timeout to avoid hanging if event doesn't fire
					setTimeout(resolve, 500);
				});
				
				await dataChannelClosePromise;
				eventsBus.emit('open-ai-realtime--events', 'info', 'Data channel closed successfully');
			} catch (error) {
				console.error("Error closing data channel:", error);
			} finally {
				dc = null;
			}
		}
		
		// Handle peer connection closure
		if (pc) {
			try {
				// Stop all tracks properly first
				pc.getSenders().forEach((sender) => {
					if (sender.track) {
						sender.track.stop();
						
						// Force release of resources
						try {
							const stream = new MediaStream([sender.track]);
							stream.removeTrack(sender.track);
						} catch (e) {
							console.warn("Error cleaning up track:", e);
						}
					}
				});
				
				pc.getReceivers().forEach((receiver) => {
					if (receiver.track) {
						receiver.track.stop();
					}
				});
				
				// Create a promise that resolves when peer connection closes or after timeout
				const peerConnectionClosePromise = new Promise((resolve) => {
					// If already closed, resolve immediately
					if (pc.connectionState === 'closed' || pc.signalingState === 'closed') {
						resolve();
						return;
					}
					
					// Set up event listener for proper closure
					const onStateChange = () => {
						if (pc.connectionState === 'closed' || pc.signalingState === 'closed') {
							pc.removeEventListener('connectionstatechange', onStateChange);
							pc.removeEventListener('signalingstatechange', onStateChange);
							resolve();
						}
					};
					pc.addEventListener('connectionstatechange', onStateChange);
					pc.addEventListener('signalingstatechange', onStateChange);
					
					// Call close (synchronous method)
					pc.close();
					
					// Add a timeout to avoid hanging if event doesn't fire
					setTimeout(resolve, 500);
				});
				
				await peerConnectionClosePromise;
				eventsBus.emit('open-ai-realtime--events', 'info', 'Peer connection closed successfully');
			} catch (error) {
				console.error("Error closing peer connection:", error);
			} finally {
				pc = null;
			}
		}
		
		mediaResources.tracks.forEach(track => {
			if (track && !track.readyState !== "ended") {
				console.log(`Stopping track: ${track.kind}, ID: ${track.id}`);
				track.stop();
			}
		});
		
		// Step 2: Disconnect and close all audio contexts
		mediaResources.audioContexts.forEach(context => {
			if (context && context.state !== "closed") {
				console.log(`Closing audio context, state: ${context.state}`);
				context.close().catch(e => console.warn("Error closing audio context:", e));
			}
		});
		
		// Step 3: Disconnect any analyser or gain nodes
		mediaResources.analyserNodes.forEach(node => {
			if (node) {
				try {
					console.log("Disconnecting analyser node");
					node.disconnect();
				} catch (e) {
					console.warn("Error disconnecting analyser node:", e);
				}
			}
		});
		
		mediaResources.gainNodes.forEach(node => {
			if (node) {
				try {
					console.log("Disconnecting gain node");
					node.disconnect();
				} catch (e) {
					console.warn("Error disconnecting gain node:", e);
				}
			}
		});
		
		// Step 4: Clear all other connections
		mediaResources.connections.forEach(connection => {
			if (connection && typeof connection.disconnect === 'function') {
				try {
					console.log("Disconnecting audio connection");
					connection.disconnect();
				} catch (e) {
					console.warn("Error disconnecting connection:", e);
				}
			}
		});
		
		// Reset the resource tracker
		mediaResources.streams = [];
		mediaResources.tracks = [];
		mediaResources.audioContexts = [];
		mediaResources.analyserNodes = [];
		mediaResources.gainNodes = [];
		mediaResources.connections = [];
		
		// Force garbage collection cycle for browsers that support it
		if (window.gc) {
			console.log("Forcing garbage collection");
			window.gc();
		}
		
		// Invalidate ephemeral key when changing voices
		ephemeralKey = null;
		
		// Add a delay to ensure everything is properly cleaned up
		await new Promise(resolve => setTimeout(resolve, 300));
		console.log("Cleanup complete, ready for new connection");
		
		// Invalidate ephemeral key when changing voices
		ephemeralKey = null;
		
		// Add a small delay to ensure everything is cleaned up before reinitialization
		if (hadDC || hadPC) {
			await new Promise(resolve => setTimeout(resolve, 200));
		}
	}

	async function initSpeaker(opts) {
		let processFuncOrGetValue = (v) => {
			return v ? (typeof(v) === 'function' ? v() : v) : null;
		}

		let instructions = processFuncOrGetValue(opts?.instructions);
		if (opts?.instructions) {
			opts.instructions = instructions;
		}

		let tools = processFuncOrGetValue(opts?.tools);
		if (opts?.tools) {
			opts.tools = tools;
		}

		let replaceVariables = (text) => {
			if (opts?.variables) {
				Object.keys(opts.variables).map(f => {
					if (f !== 'evaluate') {
						text = text.replace(new RegExp(`\\[${f}\\]`, 'g'), opts.variables[f]);
					}
				});
			}
			return text;
		}
		if (opts?.variables) {
			if (opts?.variables?.evaluate) {
				opts.variables.evaluate(opts?.variables);
			}

			instructions = replaceVariables(instructions);
		}

		if (dc && pc) {
			await closeSession();
		}
		await init(opts);

		const event = {
			type: "session.update",
			session: {
				input_audio_transcription: {
					model: "whisper-1"
				},
				instructions:
					(instructions || 'Greet user and ask what he want to talk about'),
				tools: Object.values(tools || {}).map(f => f?.definition).filter(f => f),
				"tool_choice": "auto",
			}
		};

		if (opts.language) {
			event.session.input_audio_transcription.language = opts.language.substr(0, 2);
		}

		eventsBus.emit('open-ai-realtime--events', `info`, event);

		if (__DEBUG__)
			console.debug('AIRealtimeInfo init', event);

		await sendMessage(event);
		makeResponse();
	}

	return {
		init,
		sendMessage,
		sendText,
		initSpeaker,
		closeSession,
		voices
	};
})();

export default OpenAIWebRTC;
export { getSession, authKey, voices };